import { ChevronDown, Loader2 } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { PicklistValue } from '@/api/client';
import { cn } from '@/lib/utils';
import { stageColor } from '@/lib/cobalt-helpers';

interface Props {
  stageId: number | null;
  stageOpts: PicklistValue[];
  onSelect: (stageId: number) => void;
  pending?: boolean;
  size?: 'sm' | 'md';
}

export function StagePicker({ stageId, stageOpts, onSelect, pending, size = 'md' }: Props) {
  const current = stageOpts.find((s) => Number(s.value) === stageId);
  const label   = current?.label ?? (stageId != null ? `#${stageId}` : 'No stage');
  const color   = stageColor(label);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          onClick={(e) => e.stopPropagation()}
          className={cn(
            'inline-flex max-w-full items-center gap-1.5 whitespace-nowrap rounded-full font-medium leading-tight tracking-[0.01em]',
            'transition-opacity hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            size === 'sm' ? 'px-2 py-0.5 text-[11.5px]' : 'px-2.5 py-1 text-[12px]',
          )}
          style={{
            background: `color-mix(in oklab, ${color} 18%, transparent)`,
            color,
          }}
        >
          {pending ? (
            <Loader2 className="h-2.5 w-2.5 animate-spin shrink-0" />
          ) : (
            <span className="h-1.5 w-1.5 rounded-full shrink-0" style={{ background: color }} />
          )}
          <span className="truncate" title={label}>{label}</span>
          <ChevronDown className="h-2.5 w-2.5 shrink-0 opacity-60" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-[180px]" onClick={(e) => e.stopPropagation()}>
        {stageOpts.map((opt) => {
          const optColor   = stageColor(opt.label);
          const isSelected = Number(opt.value) === stageId;
          return (
            <DropdownMenuItem
              key={opt.value}
              onSelect={() => onSelect(Number(opt.value))}
              className={cn('flex items-center gap-2', isSelected && 'font-medium')}
            >
              <span className="h-2 w-2 rounded-full shrink-0" style={{ background: optColor }} />
              {opt.label}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
